import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, MessageCircle } from "lucide-react";
import { Reveal, MaskedLine, Eyebrow } from "@/components/Motion";
import Faq from "@/components/Faq";
import { IMAGES, CONTACTS } from "@/data/content";
import { useLanguage } from "@/i18n";

export default function Home() {
  const heroRef = useRef(null);
  const { t, content } = useLanguage();
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} data-testid="hero-section" className="relative flex min-h-screen flex-col justify-end overflow-hidden">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img
            src={IMAGES.hero}
            alt="Cantiere Edilcomplex2020"
            className="h-full w-full object-cover"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#F7F5F0] via-[#F7F5F0]/55 to-[#F7F5F0]/25" />
          <div className="hero-vignette absolute inset-0" />
        </motion.div>

        <div className="relative mx-auto w-full max-w-7xl px-6 pb-10 pt-40">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <Eyebrow data-testid="hero-eyebrow">{t("heroEyebrow")}</Eyebrow>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="mt-6 font-serif text-[13vw] font-medium uppercase leading-[0.95] tracking-tight text-stone-900 sm:text-[10vw] lg:text-[7.2vw]"
          >
            <MaskedLine delay={0.35}>
              <span className="text-[#A97F2F]">{t("heroL1")}</span>
            </MaskedLine>
            <MaskedLine delay={0.47}>
              <span className="text-[#A97F2F]">{t("heroL2")}</span>
            </MaskedLine>
            <MaskedLine delay={0.59}>
              <span className="text-[#1C1A17]">{t("heroL3")}</span>
            </MaskedLine>
            <MaskedLine delay={0.71}>
              <span className="text-[#A97F2F]">{t("heroL4")}</span>
            </MaskedLine>
          </h1>

          <div className="mt-8 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
            <Reveal delay={0.9} className="max-w-xl">
              <p data-testid="hero-subheadline" className="text-base font-light leading-relaxed text-stone-600 md:text-lg">
                {t("heroSub")}
              </p>
            </Reveal>
            <Reveal delay={1.05} className="flex flex-wrap gap-4">
              <a
                data-testid="hero-whatsapp-cta"
                href={CONTACTS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 whitespace-nowrap bg-[#C89D66] px-7 py-4 font-mono text-[11px] uppercase tracking-[0.15em] text-[#0F1012] transition-colors duration-300 hover:bg-[#D4A972]"
              >
                <MessageCircle className="h-4 w-4" />
                {t("heroCtaQuote")}
              </a>
              <Link
                data-testid="hero-projects-cta"
                to="/progetti"
                className="group inline-flex items-center gap-3 border border-stone-300 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-stone-900 transition-colors duration-300 hover:border-[#C89D66] hover:text-[#8F6B3C]"
              >
                {t("heroCtaProjects")}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>

          <motion.div
            data-testid="hero-stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.25 }}
            className="mt-14 grid grid-cols-2 gap-px border-t border-stone-300/50 bg-stone-300/60 md:grid-cols-4"
          >
            {content.stats.map((stat) => (
              <div key={stat.label} className="bg-[#F7F5F0]/80 px-5 py-6 backdrop-blur-sm">
                <p className="font-serif text-3xl text-[#8F6B3C] md:text-4xl">{stat.value}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500">
                  {stat.label}
                </p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SERVIZI PREVIEW */}
      <section data-testid="services-section" className="mx-auto max-w-7xl px-6 py-24 md:py-32">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <Reveal>
              <Eyebrow>{t("servicesEyebrow")}</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 max-w-xl font-serif text-3xl uppercase leading-tight tracking-tight text-stone-900 md:text-5xl">
                {t("servicesTitleA")} <span className="italic text-[#8F6B3C]">{t("servicesTitleB")}</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <Link
              data-testid="services-view-all-link"
              to="/servizi"
              className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-stone-600 transition-colors hover:text-[#8F6B3C]"
            >
              {t("viewAllServices")}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-px bg-stone-300/60 sm:grid-cols-2 lg:grid-cols-3">
          {content.services.map((service, i) => (
            <motion.div
              key={service.id}
              data-testid={`service-card-${service.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.12, ease: [0.22, 1, 0.36, 1] }}
            >
              <Link
                to={`/servizi#${service.id}`}
                className="group flex h-full flex-col justify-between bg-[#F7F5F0] p-8 transition-colors duration-500 hover:bg-[#FFFFFF] md:p-10"
              >
                <div>
                  <span className="font-mono text-xs tracking-[0.25em] text-[#8F6B3C]/80">
                    {service.num}
                  </span>
                  <h3 className="mt-5 font-serif text-2xl leading-snug text-stone-900 transition-colors duration-300 group-hover:text-[#8F6B3C] md:text-[1.7rem]">
                    {service.title}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-stone-500">{service.short}</p>
                </div>
                <div className="mt-8 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500 transition-colors duration-300 group-hover:text-[#8F6B3C]">
                  {t("discover")}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* IMAGE BREAK */}
      <section data-testid="image-break" className="relative h-[62vh] overflow-hidden md:h-[75vh]">
        <motion.img
          src={IMAGES.cantiere}
          alt="Cantiere Edilcomplex2020"
          className="h-[120%] w-full object-cover"
          initial={{ scale: 1.15 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        />
        <div className="absolute inset-0 bg-[#1C1A17]/55" />
        <div className="absolute inset-0 flex items-center justify-center px-6">
          <Reveal>
            <p className="max-w-3xl text-center font-serif text-3xl italic leading-snug text-[#F7F5F0] md:text-5xl">
              {t("quoteA")}
              <span className="text-[#D4A972]">{t("quoteB")}</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* PERCHÉ NOI */}
      <section data-testid="pillars-section" className="mx-auto max-w-7xl px-6 py-24 md:py-32">
        <Reveal>
          <Eyebrow>{t("pillarsEyebrow")}</Eyebrow>
        </Reveal>
        <div className="mt-12 grid gap-px bg-stone-300/60 md:grid-cols-2 lg:grid-cols-4">
          {content.pillars.map((pillar, i) => (
            <motion.div
              key={pillar.title}
              data-testid={`pillar-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="group bg-[#F7F5F0] p-8 transition-colors duration-500 hover:bg-[#FFFFFF]"
            >
              <span className="block h-px w-10 bg-[#C89D66] transition-all duration-500 group-hover:w-20" />
              <h3 className="mt-6 font-serif text-xl uppercase tracking-wide text-stone-900">
                {pillar.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-stone-500">{pillar.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* STRISCIA FOTO CANTIERI */}
      <section data-testid="gallery-strip" className="mx-auto max-w-7xl px-6 pb-24 md:pb-32">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
          {[
            { src: content.projects[2].images[1], alt: content.projects[2].title },
            { src: content.projects[0].images[3], alt: content.projects[0].title },
            { src: content.projects[1].images[1], alt: content.projects[1].title },
            { src: IMAGES.dettaglio, alt: "Dettaglio costruttivo in cantiere" },
          ].map((photo, i) => (
            <Reveal key={i} delay={i * 0.08} className={i % 2 === 1 ? "lg:translate-y-8" : ""}>
              <div className="group overflow-hidden">
                <img
                  src={photo.src}
                  alt={photo.alt}
                  className="img-duotone aspect-[3/4] w-full object-cover"
                  loading="lazy"
                />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PROGETTI RECENTI */}
      <section data-testid="recent-projects-section" className="border-t border-stone-300/70 bg-[#F0ECE4]">
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-32">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <Reveal>
                <Eyebrow>{t("projectsEyebrow")}</Eyebrow>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-4 font-serif text-3xl uppercase leading-tight tracking-tight text-stone-900 md:text-5xl">
                  {t("projectsTitleA")} <span className="italic text-[#8F6B3C]">{t("projectsTitleB")}</span>
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <Link
                data-testid="projects-view-all-link"
                to="/progetti"
                className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-stone-600 transition-colors hover:text-[#8F6B3C]"
              >
                {t("viewAllProjects")}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </Reveal>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-12">
            <Reveal className="md:col-span-7">
              <Link to="/progetti" data-testid="recent-project-1" className="group block overflow-hidden">
                <div className="overflow-hidden">
                  <img src={content.projects[0].images[0]} alt={content.projects[0].title} className="img-duotone aspect-[3/2] w-full object-cover" />
                </div>
                <div className="flex items-center justify-between border-b border-stone-300 py-4">
                  <span className="font-serif text-xl text-stone-900">{content.projects[0].title}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500">{content.projects[0].category}</span>
                </div>
              </Link>
            </Reveal>
            <div className="flex flex-col gap-6 md:col-span-5">
              <Reveal delay={0.12}>
                <Link to="/progetti" data-testid="recent-project-2" className="group block overflow-hidden">
                  <div className="overflow-hidden">
                    <img src={content.projects[2].images[0]} alt={content.projects[2].title} className="img-duotone aspect-[4/5] w-full object-cover" />
                  </div>
                  <div className="flex items-center justify-between border-b border-stone-300 py-4">
                    <span className="font-serif text-xl text-stone-900">{content.projects[2].title}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500">{content.projects[2].category}</span>
                  </div>
                </Link>
              </Reveal>
            </div>
            <Reveal className="md:col-span-12" delay={0.18}>
              <Link to="/progetti" data-testid="recent-project-3" className="group block overflow-hidden">
                <div className="overflow-hidden">
                  <img src={content.projects[1].images[0]} alt={content.projects[1].title} className="img-duotone aspect-[16/10] w-full object-cover md:aspect-[21/9]" />
                </div>
                <div className="flex items-center justify-between border-b border-stone-300 py-4">
                  <span className="font-serif text-xl text-stone-900">{content.projects[1].title}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500">{content.projects[1].category}</span>
                </div>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <Faq />

      {/* CTA FINALE */}
      <section data-testid="cta-section" className="relative overflow-hidden border-t border-stone-300/70">
        <div className="absolute inset-0">
          <img src={IMAGES.recente3} alt="" className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#F7F5F0] via-[#F7F5F0]/80 to-[#F7F5F0]" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 py-28 text-center md:py-36">
          <Reveal>
            <Eyebrow className="justify-center">{t("ctaEyebrow")}</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mx-auto mt-6 max-w-3xl font-serif text-4xl uppercase leading-[1.05] tracking-tight text-stone-900 md:text-6xl">
              {t("ctaTitleA")} <span className="italic text-[#8F6B3C]">{t("ctaTitleB")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-stone-500">
              {t("ctaSub")}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a
                data-testid="cta-whatsapp-btn"
                href={CONTACTS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 whitespace-nowrap bg-[#C89D66] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.15em] text-[#0F1012] transition-colors duration-300 hover:bg-[#D4A972]"
              >
                <MessageCircle className="h-4 w-4" />
                {t("ctaWhatsApp")}
              </a>
              <Link
                data-testid="cta-contacts-btn"
                to="/contatti"
                className="inline-flex items-center gap-3 border border-stone-300 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-stone-900 transition-colors duration-300 hover:border-[#C89D66] hover:text-[#8F6B3C]"
              >
                {t("ctaContacts")}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
